import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faBug,
  faExclamationTriangle,
  faArrowRight,
  faCheckCircle,
  faBell,
  faTools,
  faChartBar,
  faCogs,
  faCircleInfo,
  faListCheck,
  faDatabase,
  faFileAlt,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import "../Home/services.css";
import PageHeader from "../../componentes/PageHeader";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const ProductDetails = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const decodedProductId = decodeURIComponent(productId);
  const backgroundIcons = [
    faLock,
    faBug,
    faExclamationTriangle,
    faArrowRight,
    faCheckCircle,
    faBell,
    faTools,
    faChartBar,
    faCogs,
    faCircleInfo,
    faListCheck,
    faDatabase,
    faFileAlt,
    faShieldAlt,
  ];

  const productDetails = {
    National_Cyber_Threat_Intelligence_Database: {
      icon: faDatabase,
      title: t("home.our_products.product1.title"),
      description: t("home.our_products.product1.description"),
      fullDescription: t("home.our_products.product1.fullDescription"),
      features: [
        t("home.our_products.product1.feature1"),
        t("home.our_products.product1.feature2"),
        t("home.our_products.product1.feature3"),
        t("home.our_products.product1.feature4"),
        t("home.our_products.product1.feature5"),
      ],
    },
    National_Cyber_Incident_Analysis_Reports: {
      icon: faFileAlt,
      title: t("home.our_products.product2.title"),
      description: t("home.our_products.product2.description"),
      fullDescription: t("home.our_products.product2.fullDescription"),
      features: [
        t("home.our_products.product2.feature1"),
        t("home.our_products.product2.feature2"),
        t("home.our_products.product2.feature3"),
        t("home.our_products.product2.feature4"),
        t("home.our_products.product2.feature5"),
      ],
    },
    National_Incident_Reporting_Management_Platform: {
      icon: faShieldAlt,
      title: t("home.our_products.product3.title"),
      description: t("home.our_products.product3.description"),
      fullDescription: t("home.our_products.product3.fullDescription"),
      features: [
        t("home.our_products.product3.feature1"),
        t("home.our_products.product3.feature2"),
        t("home.our_products.product3.feature3"),
        t("home.our_products.product3.feature4"),
        t("home.our_products.product3.feature5"),
      ],
    },
  };

  const product = productDetails[decodedProductId];

  if (!product) {
    return <div>Product not found</div>;
  }

  return (
    <>
      <PageHeader />
      <div className="max-xl:pl-4 bg-gradient-to-r from-midnigt to-shake pt-24 px-dynamic max-sm:h-56 max-md:h-64 h-72 relative overflow-hidden">
        {backgroundIcons.length > 0 && (
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  opacity: 0.1,
                }}
              >
                <FontAwesomeIcon
                  icon={backgroundIcons[i % backgroundIcons.length]}
                  className="text-white text-4xl"
                />
              </div>
            ))}
          </div>
        )}

        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-wrap items-center gap-2 text-white/80 text-sm md:mb-12 max-xl:mt-8"
          >
            <Link to="/" className="hover:text-white transition-colors">
              {t("header.home")}
            </Link>
            <span>/</span>
            <Link
              to="/our-service"
              className="hover:text-white transition-colors"
            >
              {t("header.our_services")}
            </Link>
            <span>/</span>
            <span className="break-words">{product.title}</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex items-center gap-4 flex-wrap sm:flex-nowrap">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="w-12 h-12 sm:w-14 sm:h-14 bg-white/10 rounded-full flex items-center justify-center"
              >
                <FontAwesomeIcon
                  icon={product.icon}
                  className="text-2xl sm:text-3xl text-white"
                />
              </motion.div>
              <div className="flex-1 min-w-0">
                <motion.h1
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="text-h-5 max-xl:mt-2 max-sm:text-base font-bold text-white tracking-tight font-catamaran break-words"
                >
                  {product.title}
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="hidden md:block text-gray-200 mt-2 max-sm:text-sm text-body-big font-medium font-nunito-sans break-words whitespace-normal"
                >
                  {product.description}
                </motion.p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="mx-4 md:mx-8 xl:mx-32 mt-20">
        <div className="relative">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-8">
              <div className="mb-12 bg-blue-50 p-4 rounded-2xl hover:shadow-xl transition-shadow duration-300">
                <h2 className="text-h-6 font-catamaran font-bold mb-6 flex items-center">
                  <span className="w-8 h-8 bg-primary rounded-full mr-3 flex items-center justify-center text-white">
                    <FontAwesomeIcon icon={faCircleInfo} className="text-sm" />
                  </span>
                  {t("home.our_services.over_view")}
                </h2>
                <p className="text-body-big font-nunito-sans leading-relaxed text-gray-700">
                  {product.fullDescription}
                </p>
              </div>

              <div className="mb-12 p-4">
                <h2 className="text-h-6 font-catamaran font-bold mb-8 flex items-center">
                  <span className="w-8 h-8 bg-primary rounded-full mr-3 flex items-center justify-center text-white">
                    <FontAwesomeIcon icon={faListCheck} className="text-sm" />
                  </span>
                  {t("home.our_services.key_feauters")}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {product.features.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-start py-6 px-3 bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 border border-gray-100"
                    >
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="text-primary text-xl mr-4 mt-1"
                      />
                      <p className="text-gray-700 text-body-big font-nunito-sans text-justify">
                        {feature}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="col-span-12 lg:col-span-4 max-lg:hidden pb-12">
              <div className="sticky top-24 max-xl:top-32">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                  className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-2xl p-6 lg:p-8 relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 opacity-10">
                    <svg
                      width="200"
                      height="200"
                      viewBox="0 0 200 200"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill="#4F46E5"
                        d="M40,0 L60,0 L100,40 L40,100 L0,60 Z"
                      />
                      <circle cx="150" cy="50" r="30" fill="#4F46E5" />
                      <rect
                        x="120"
                        y="100"
                        width="60"
                        height="60"
                        fill="#4F46E5"
                      />
                    </svg>
                  </div>

                  <h3 className="text-xl font-bold mb-8 text-gray-800 border-b pb-4">
                    {t("home.our_products.other_products")}
                  </h3>
                  <div className="space-y-4 relative z-10">
                    {Object.entries(productDetails).map(([key, product]) => {
                      if (key === decodedProductId) return null;

                      return (
                        <div
                          key={key}
                          className="group cursor-pointer hover:bg-gray-50 p-3 rounded-xl transition-all duration-300"
                          onClick={() => navigate(`/products/${key}`)}
                        >
                          <div className="flex items-center space-x-4">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                              <FontAwesomeIcon
                                icon={product.icon}
                                className="text-primary text-xl"
                              />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium font-nunito-sans text-gray-800 group-hover:text-primary transition-colors duration-300">
                                {product.title}
                              </h4>
                              <p className="text-sm text-gray-600 line-clamp-2">
                                {product.description}
                              </p>
                            </div>
                            <FontAwesomeIcon
                              icon={faArrowRight}
                              className="text-primary text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductDetails;